import {
    Component,
    OnInit,
    OnDestroy,
    ElementRef,
    ViewChild,
    Input,
    Output,
    EventEmitter,
    SimpleChanges, OnChanges, HostListener
} from '@angular/core';
import {Editor} from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {Color} from '@tiptap/extension-color';
import {IconsComponent} from "@library/shared/icons/icons.component";
import {ReactiveFormsModule, UntypedFormGroup} from "@angular/forms";
import {CommonModule} from "@angular/common";
import {PickerComponent} from "@ctrl/ngx-emoji-mart";
import {CloudFile, EAIGenerationStatus, round} from "@nxt/model-core";
import {PageService} from "@library/shared/_services/page.service";
import {ClientService} from "@library/shared/_services/client.service";
import {FireService} from "@library/nxt/_services/fire.service";
import {TextStyle} from "@tiptap/extension-text-style";
import {UserService} from "@library/nxt/_services/user.service";
import {HtmlContentStylesPipe} from "@library/shared/_pipes/pipes";
import {OnDestroyPage} from "@library/shared/_inherited/ondestroy.page";

@Component({
    selector: 'rich-text-editor',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        IconsComponent,
        PickerComponent,
        HtmlContentStylesPipe,
    ],
    standalone: true,
    template: `
        <div #editorContent
             class="tiptap overflow-y-auto text-base leading-6 mt-3 p-4 rounded-t-lg bg-white text-black border border-gray-300"></div>
        <div class="relative">
            <div class="w-full border border-gray-300 border-t-0 rounded-b-lg bg-gray-100">
                <div class="flex flex-wrap gap-2 origin-top">
                    <button (click)="executeCommand('bold')" class="toolbar-btn"
                            [class.active]="editor?.isActive('bold')">
                        <icon name="heroicon-solid-bold" class="h-5 w-5"></icon>
                    </button>
                    <button (click)="executeCommand('italic')" class="toolbar-btn"
                            [class.active]="editor?.isActive('italic')">
                        <icon name="heroicon-solid-italic" class="h-5 w-5"></icon>
                    </button>
                    <button (click)="executeCommand('underline')" class="toolbar-btn"
                            [class.active]="editor?.isActive('underline')">
                        <icon name="heroicon-outline-underline" class="h-5 w-5"></icon>
                    </button>
                    <div class="relative">
                        <button class="toolbar-btn inline-flex items-center justify-center space-x-1 p-1"
                                (click)="openColorPicker()">
                            <span class="mt-0.5 w-5 h-5 rounded border border-gray-400"
                                  [style.background]="selectedColor"></span>
                        </button>
                        <input
                            type="color"
                            #colorPicker
                            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            (input)="changeFontColor($event)"
                        />
                    </div>
                    <button (click)="toggleEmojiPicker($event)" class="toolbar-btn relative">
                        😊
                    </button>
                    <button (click)="executeCommand('highlight')" class="toolbar-btn"
                            [class.active]="editor?.isActive('highlight')">
                        <icon name="custom-highlight" class="h-5 w-5"></icon>
                    </button>
                    <button (click)="executeCommand('link')" class="toolbar-btn"
                            [class.active]="editor?.isActive('link')">
                        <icon name="heroicon-outline-link" class="h-5 w-5"></icon>
                    </button>
                    <button (click)="executeCommand('bullet-list')" class="toolbar-btn"
                            [class.active]="editor?.isActive('bulletList')">
                        <icon name="heroicon-outline-list-bullet" class="h-5 w-5"></icon>
                    </button>
                    <button class="toolbar-btn" (click)="fileUpload.click()">
                        <icon name="heroicon-outline-paper-clip" class="h-5 w-5"></icon>
                    </button>
                    <button *ngIf="AI"
                            (click)="convertMsg(form.get(controlName).value)"
                            class="btn-ai"
                            [class.animate-pulse]="isPolishing"
                            [disabled]="isPolishing">
                        <img src="/assets/apis/nxt_ai.png"
                             alt="Toggle AI"
                             class="ai-icon">
                    </button>
                </div>
            </div>
            <div #emojiPickerContainer *ngIf="showEmojiPicker"
                 class="absolute top-12 left-0 z-50 bg-white shadow-lg border border-gray-300 rounded-lg">
                <emoji-mart
                    set="apple"
                    [perLine]="7"
                    [emojiSize]="24"
                    [showPreview]="false"
                    (emojiClick)="insertEmoji($event.emoji.native)">
                </emoji-mart>
            </div>
        </div>
        <div *ngIf="showPolishSuggestion"
             class="mt-4 overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-sm">
            <div class="flex items-center justify-between border-b border-blue-100 bg-white/60 px-4 py-3 backdrop-blur-sm">
                <div class="flex items-center gap-2">
                    <div class="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
                        <svg class="h-3.5 w-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                        </svg>
                    </div>
                    <h4 class="text-sm font-semibold text-gray-900">AI Suggestion</h4>
                </div>
                <button
                    class="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    (click)="showPolishSuggestion = false"
                    title="Dismiss suggestion">
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>
            <div class="p-4">
                <div class="relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div class="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-400 to-indigo-500"></div>
                    <div [class]="'prose prose-sm max-w-none px-4 py-3 text-gray-700' | htmlContentStyles"
                         [innerHTML]="polishedHtml"></div>
                </div>
            </div>
            <div class="border-t border-blue-100 bg-white/40 px-4 py-3 backdrop-blur-sm">
                <div class="flex items-center justify-between gap-3">
                    <div class="text-xs text-gray-500">Review the AI-generated suggestion above</div>
                    <div class="flex gap-2">
                        <button
                            class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            (click)="showPolishSuggestion = false">
                            Dismiss
                        </button>
                        <button
                            class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            (click)="convertMsg(form.get(controlName).value)"
                            [disabled]="isPolishing">
                            <svg class="h-3.5 w-3.5 transition-transform"
                                 [class.animate-spin]="isPolishing"
                                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                            </svg>
                            <span>{{ isPolishing ? 'Loading...' : 'Regenerate' }}</span>
                        </button>
                        <button
                            class="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:from-blue-700 hover:to-indigo-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            (click)="applyPolish()">
                            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M5 13l4 4L19 7"></path>
                            </svg>
                            Accept
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <input type="file" #fileInput (input)="handleFileInput($event)" [accept]="accept" #fileUpload class="hidden">
    `,
    styles: [`
        .toolbar-btn {
            @apply p-2 text-gray-600 rounded hover:bg-gray-200 transition;
        }
        .toolbar-btn.active {
            @apply bg-gray-400 text-white;
        }
    `]
})
export class RichTextEditorComponent extends OnDestroyPage implements OnInit, OnChanges, OnDestroy {
    @ViewChild('fileInput') fileInput: ElementRef;
    @Input() form: UntypedFormGroup;
    @Input() controlName: string;
    @Input() parent: any;
    @Input() label: string;
    @Input() path: string;
    @Input() height: string;
    @Input() disallowUpload: boolean;
    @Input() maxFileSize: number = 10240000;
    @Input() allowedFiles: string[];
    @Input() disallowedFiles: string[] = ['heic', 'exe', 'dll', 'bin', 'sh'];
    @Input() randomizeFilePath: boolean = false;
    @Input() AI: boolean = false;
    @Input() aiTaskLabel: string = 'polish_message';
    @Output() onKeyup: EventEmitter<any> = new EventEmitter<any>();
    @Output() onFileUpload: EventEmitter<CloudFile> = new EventEmitter<CloudFile>();
    @ViewChild('editorContent', {static: true}) editorContent!: ElementRef;
    @ViewChild('colorPicker', {static: false}) colorPicker!: ElementRef;
    @ViewChild('emojiPickerContainer', {static: false}) emojiPickerContainer!: ElementRef;

    editor!: Editor;
    showEmojiPicker: boolean = false;
    selectedColor: string = '#000000';
    promptOpen: boolean = false;
    editorHeight: string = '200px';
    isPolishing = false;
    polishedHtml: string | null = null;
    showPolishSuggestion = false;

    get sizeLimit(): string {
        return this.maxFileSize ? `${round(this.maxFileSize / 1000, 0)}kb/${round(this.maxFileSize / 1000 / 1000, 2)}mb` : 'any size';
    }

    get accept(): string {
        return this.allowedFiles?.join ? this.allowedFiles.join(',') : '';
    }

    constructor(
        private pSvc: PageService,
        private cSvc: ClientService,
        private fSvc: FireService,
        public uSvc: UserService,
    ) {
        super();
    }

    ngOnInit() {
        this.updateEditorHeight();
        this.editor = new Editor({
            element: this.editorContent?.nativeElement,
            extensions: [
                StarterKit,
                Highlight,
                Underline,
                Link.configure({
                    openOnClick: true,
                    linkOnPaste: true,
                    defaultProtocol: 'https',
                    HTMLAttributes: {
                        class: 'editor-link',
                    }
                }),
                TextStyle,
                Color.configure({types: ['textStyle']}),
                Placeholder.configure({
                    emptyEditorClass: 'is-empty',
                    placeholder: `${this.label}`,
                    showOnlyWhenEditable: true,
                    includeChildren: true,
                    emptyNodeClass: 'empty-node',
                })
            ],
            content: this.form.get(this.controlName)?.value,
            editorProps: {
                attributes: {
                    class: 'focus:outline-none w-full',
                    spellcheck: 'true',
                    style: `min-height: ${this.editorHeight};`,
                },
            },
            onUpdate: ({editor}) => {
                this.form.get(this.controlName)?.setValue(editor.getHTML());
                this.onKeyup.emit(editor.getHTML());
            }
        });

        this.editorContent.nativeElement.addEventListener('dragover', (event: DragEvent) => {
            event.preventDefault();
        });

        this.editorContent.nativeElement.addEventListener('drop', (event: DragEvent) => {
            event.preventDefault();
            event.stopPropagation();
            this.handleFileDrop(event);
        });

        this.editorContent.nativeElement.addEventListener('paste', async (event: ClipboardEvent) => {
            event.preventDefault();
            const items = event.clipboardData?.items;
            if (!items) return;
            for (const item of Array.from(items)) {
                const file = item.getAsFile();
                if (file) await this.handleFiles([file]);
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.form && this.editor) {
            this.editor.commands.setContent(this.form.get(this.controlName)?.value || '');
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.editor.destroy();
    }

    executeCommand(command: string) {
        switch (command) {
            case 'bold':
                this.editor.chain().focus().toggleBold().run();
                break;
            case 'italic':
                this.editor.chain().focus().toggleItalic().run();
                break;
            case 'underline':
                this.editor.chain().focus().toggleMark('underline').run();
                break;
            case 'highlight':
                this.editor.chain().focus().toggleMark('highlight').run();
                break;
            case 'link':
                this.promptOpen = true;
                let url = prompt('Enter URL:');
                this.promptOpen = false;
                if (url) {
                    url = url.trim();
                    if (!/^https?:\/\//i.test(url)) {
                        this.pSvc.notification$.next({
                            title: 'We Changed Your Link!',
                            message: `The link must start with http or https. We guessed, and added https. If this is incorrect, please edit the link manually.`
                        });
                        url = `https://${url}`;
                    }
                    this.editor.chain().focus().extendMarkRange('link').setLink({href: url}).run();
                }
                this.editor.commands.focus();
                break;
            case 'bullet-list':
                this.editor.chain().focus().toggleBulletList().run();
                break;
        }
    }

    updateEditorHeight() {
        if (this.height) {
            this.editorHeight = this.height;
        } else if (this.parent?.medium) {
            switch (this.parent.medium) {
                case 'email':
                    this.editorHeight = '250px';
                    break;
                case 'chat':
                    this.editorHeight = '100px';
                    break;
                case 'note':
                    this.editorHeight = '150px';
                    break;
                default:
                    this.editorHeight = '200px';
                    break;
            }
        }
    }

    openColorPicker() {
        this.colorPicker.nativeElement.click();
    }

    changeFontColor(event: Event) {
        const target = event.target as HTMLInputElement;
        if (target) {
            this.selectedColor = target.value;
            this.editor.chain().focus().setColor(this.selectedColor).run();
        }
    }

    toggleEmojiPicker(event: Event) {
        event.stopPropagation();
        this.showEmojiPicker = !this.showEmojiPicker;
    }

    insertEmoji(emoji: string) {
        this.editor.chain().focus().insertContent(emoji).run();
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event) {
        if (
            this.showEmojiPicker &&
            this.emojiPickerContainer &&
            this.emojiPickerContainer.nativeElement &&
            !this.emojiPickerContainer.nativeElement.contains(event.target)
        ) {
            this.showEmojiPicker = false;
        }
    }

    async handleFileDrop(event: DragEvent) {
        if (this.parent?.id) {
            if (!event.dataTransfer || !event.dataTransfer.files.length) return;
            event.preventDefault();
            event.stopPropagation();
            const files = Array.from(event.dataTransfer.files);
            this.handleFiles(files);
        }
    }

    async handleFileInput(event: any) {
        const files: File[] = Array.from(event.target.files || []);
        this.handleFiles(files);
        this.fileInput.nativeElement.value = '';
    }

    async handleFiles(files: File[]) {
        if (this.disallowUpload) {
            this.pSvc.notification$.next({
                title: 'Not Allowed!',
                message: 'File uploading not enabled here.'
            });
        } else {
            for (const file of files) {
                if (this.path) {
                    try {
                        let filePath = `${this.path}/${file.name}`;
                        if (this.randomizeFilePath) {
                            filePath = `${this.path}/${Date.now()}/${file.name}`;
                        }
                        let fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

                        if (this.disallowedFiles?.length && this.disallowedFiles.includes(fileExtension)) {
                            if (fileExtension === 'heic') {
                                this.pSvc.notification$.next({
                                    title: 'Cannot Drag & Drop iOS Screenshots',
                                    message: `Instead, please open the screenshot and then copy-paste it into the text editor or save it to your device and then click the paper clip icon.`
                                });
                            } else {
                                this.pSvc.notification$.next({
                                    title: 'Disallowed File Type',
                                    message: `The file ${file.name} is not allowed to be uploaded.`
                                });
                            }
                            continue;
                        }

                        let re: RegExp = new RegExp(`\.(${this.allowedFiles?.join('|') || ''})$`, 'i');
                        if (this.allowedFiles?.length && !re.test(file.name)) {
                            this.pSvc.notification$.next({
                                title: 'Unsupported File Type',
                                message: `The file ${file.name} has an unsupported format. Allowed files include: ${this.allowedFiles.join(', ')}`,
                            });
                        } else if (this.maxFileSize && file.size > this.maxFileSize) {
                            this.pSvc.notification$.next({
                                title: 'File Size Alert',
                                message: `File is too large.  Max file size allowed is ${this.sizeLimit}`
                            });
                        } else {
                            this.pSvc.loading$.next(true);
                            const fileUrl = await this.fSvc.upload(filePath, file);
                            const cloudFile: CloudFile = new CloudFile({
                                name: file.name,
                                url: fileUrl,
                            });
                            if (!this.parent.files) {
                                this.parent.files = [];
                            }
                            cloudFile.setID();
                            this.parent.files.push(cloudFile);
                            if (!this.parent._docRef) {
                                this.parent._docRef = this.cSvc.client$.getValue()._docRef.collection(this.parent._type).doc(this.parent.id);
                            }
                            await cloudFile.save(this.parent);
                            this.onFileUpload.emit(cloudFile);
                            this.pSvc.loading$.next(false);
                        }
                    } catch (error) {
                        console.error('File upload error:', error);
                        this.pSvc.alert$.next({title: 'Upload Error', message: error.message});
                    }
                } else {
                    console.error(`No File Path Assigned`);
                }
            }
        }
    }

    async convertMsg(html: string) {
        const plainText = html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s+/g, '').trim();
        if (!plainText) {
            this.pSvc.notification$.next({
                title: 'AI Polish Error',
                message: 'Please enter a message before using AI polish.',
            });
            return;
        }

        this.isPolishing = true;
        this.showPolishSuggestion = false;
        this.polishedHtml = null;
        this.pSvc.notification$.next({
            title: 'Polishing Message...',
            message: 'Generating AI suggestion...',
        });

        try {
            const user = this.uSvc.user$.getValue();
            const res = await this.cSvc.callAPI(`ai/core/${this.aiTaskLabel}`, 'post', {
                variables: {
                    query: html,
                    user:  user ? {
                        first_name: user.first_name,
                        last_name:  user.last_name,
                        name:       `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
                    } : undefined,
                },
            });

            if (res?.error || !res?.aiGenerationPath) {
                this.pSvc.notification$.next({
                    title: 'AI Error',
                    message: res?.error || 'No generation path returned.',
                });
                this.isPolishing = false;
                return;
            }

            let done = false;
            // watchObject(path, destroy$, persistent) — pass d$ so it auto-cleans up,
            // persistent=true so nav$ doesn't kill it mid-generation
            this.fSvc.watchObject(res.aiGenerationPath, this.d$, true)
                .subscribe({
                    next: ([aiGen]: [any, boolean]) => {
                        if (!aiGen || done) return;

                        if (aiGen.status === EAIGenerationStatus.COMPLETE) {
                            done = true;
                            this.isPolishing = false;
                            // outputData is the polished HTML string from parseAIResponse
                            const polished = aiGen.outputData ?? aiGen.currentResponse;
                            if (polished) {
                                this.polishedHtml = typeof polished === 'string'
                                    ? polished
                                    : JSON.stringify(polished);
                                this.showPolishSuggestion = true;
                            }
                        } else if (aiGen.status === EAIGenerationStatus.ERROR) {
                            done = true;
                            this.isPolishing = false;
                            this.pSvc.notification$.next({
                                title: 'AI Polish Error',
                                message: aiGen.error || 'Polish failed.',
                            });
                        }
                    },
                    error: (err: any) => {
                        this.isPolishing = false;
                        this.pSvc.notification$.next({
                            title: 'AI Polish Error',
                            message: err?.message || 'Unknown error.',
                        });
                    },
                });

        } catch (err: any) {
            this.isPolishing = false;
            this.pSvc.notification$.next({
                title: 'AI Polish Error',
                message: err?.message || 'There was an issue generating the polished version.',
            });
        }
    }

    applyPolish() {
        if (this.polishedHtml) {
            this.editor.commands.setContent(this.polishedHtml);
            this.form.get(this.controlName)?.setValue(this.polishedHtml);
            this.showPolishSuggestion = false;
            this.polishedHtml = null;
        }
    }
}
